import { useMemo, useState, useEffect, useCallback } from 'react';
import { useRoles } from '../../../../../hooks/api';
import { RoleScope } from '../../../../../../typings/enums';
import { getRoleSelectOptions } from '../../../../../helpers/roles';
import type { SelectOption } from '../../../../../types';

/**
 * Hook which provides a list of roles and manages the state
 * of the selected role.
 *
 * Note that this will always auto-select the first role
 * when the roles load. Feel free to change this if you
 * need to, so long as the default behavior is to select
 * the first role.
 *
 * This can become a controlled hook if you pass in
 * a `selectedRoleId` prop.
 *
 * @param {number} [selectedRoleId] - The ID of the role to be initially selected.
 */
const useSelectRoles = (selectedRoleId?: number) => {
  const { data: roles, isLoading } = useRoles();

  const [selectedRole, setSelectedRole] = useState<SelectOption<number> | null>(null);
  //  Memoized so that we don't sort/filter every render.
  const roleOptions = useMemo(() => getRoleSelectOptions(roles || [], RoleScope.Team), [roles]);
  const onChangeRole = useCallback((role: SelectOption<number> | null) => setSelectedRole(role), [setSelectedRole]);
  //  This is used after we create a team to reset the selected role to the first role option.
  const resetSelectedRole = useCallback(() => setSelectedRole(roleOptions[0] ?? null), [setSelectedRole, roleOptions]);

  //  One-time effect to set the selected role to the first role option once they load
  useEffect(() => {
    if (roleOptions?.length && !selectedRole) {
      resetSelectedRole();
    }
  }, [roleOptions, resetSelectedRole, selectedRole]);

  //  If using this hook as a controlled component, we reset the selected role to the selected role ID if selectedRoleId changes.
  useEffect(() => {
    if (selectedRoleId) {
      setSelectedRole(roleOptions.find(role => role.value === selectedRoleId) ?? null);
    }
  }, [selectedRoleId, roleOptions]);

  return {
    roleOptions,
    isLoading,
    selectedRole,
    onChangeRole,
    resetSelectedRole,
  };
};

export default useSelectRoles;
